import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import type { MoreOptionButtonProps } from "@/interfaces";

const MoreOptionTabButton = ({
  title,
  onClick,
  isAvailable,
  icon,
  isActive = false,
  themeColor = "#49BDF3",
}: MoreOptionButtonProps & { isActive?: boolean; themeColor?: string }) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    // Trigger animation only for "More Options" button on mount, and only when not active
    if (title === "More Options" && !isActive) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, 500); // Small delay before starting animation

      return () => clearTimeout(timer);
    }
  }, [title, isActive]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Button
      size="box"
      type="button"
      variant="default"
      onClick={handleClick}
      disabled={isAvailable}
      className="rounded-lg gap-[0px] transition-all duration-200 bg-transparent hover:bg-transparent border-none shadow-none p-0 h-auto min-h-0"
    >
      <div className="flex flex-col gap-0 items-center justify-center w-full ">
        <div 
          className={`${title === "Card" ? "pl-0.5" : ""} ${title === "One Click" ? "ml-2" : ""} ${
            title === "More Options" && shouldAnimate && !isActive ? "more-options-animate" : ""
          }`}
          style={isActive ? { color: themeColor } : {}}
        >
          {icon}
        </div>
        <h3
          className="text-[11px] font-normal leading-none"
          style={{ color: isActive ? themeColor : "#979797" }}
        >
          {title}
        </h3>
      </div>
      
      {/* Add CSS animations inline for the More Options button */}
      {title === "More Options" && (
        <style>
          {`
            @keyframes fillKlarna {
              0%, 3% { fill: #979797; }
              5%, 25% { fill: #FFB3C7; }
              28%, 100% { fill: #979797; }
            }
            
            @keyframes fillLink {
              0%, 28% { fill: #979797; }
              30%, 50% { fill: #00D66F; }
              53%, 100% { fill: #979797; }
            }
            
            @keyframes fillAmazon {
              0%, 53% { fill: #979797; }
              55%, 75% { fill: #FF9900; }
              78%, 100% { fill: #979797; }
            }
            
            /* Target the three circles in the More Options icon */
            .more-options-animate svg circle:nth-of-type(1) {
              animation: fillAmazon 1s ease-in-out 1;
              animation-fill-mode: forwards;
            }
            
            .more-options-animate svg circle:nth-of-type(2) {
              animation: fillLink 1s ease-in-out 1;
              animation-fill-mode: forwards;
            }
            
            .more-options-animate svg circle:nth-of-type(3) {
              animation: fillKlarna 1s ease-in-out 1;
              animation-fill-mode: forwards;
            }
          `}
        </style>
      )}
    </Button>
  );
};

export default React.memo(MoreOptionTabButton);
